<script setup lang="ts">
  import { ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSettingsCard from '@/components/pos-v2/ui/PosUiSettingsCard.vue';
  import V2ReservationsNotice from '../V2ReservationsNotice.vue';
  import { useReservationsPublicLink } from '@/composition/reservations/use-reservations-public-link';
  import type { IReservationSettings } from '@/interfaces/IReservationSettings';

  const props = defineProps<{ value: IReservationSettings }>();
  const sharingOpen = ref(false);
  const {
    url,
    downloading,
    copy: copyLink,
    downloadQr,
  } = useReservationsPublicLink(() => props.value.branchId);
</script>

<template>
  <PosUiSettingsCard
    class="link-settings"
    :title="$t('reservations.settings.nav.link')"
    :description="$t('reservations.link.description')"
  >
    <V2ReservationsNotice
      v-if="!value.enabled || !value.onlineEnabled"
      :text="
        $t(
          !value.onlineEnabled
            ? 'reservations.notice.onlineOff'
            : 'reservations.link.disabled'
        )
      "
    />
    <label class="visually-hidden" for="rsv-public-link">{{
      $t('reservations.settings.nav.link')
    }}</label>
    <PosUiInput id="rsv-public-link" :value="url" readonly />
    <div class="link-actions">
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="content-copy"
        :disabled="!url"
        @click="copyLink"
        >{{ $t('reservations.link.copy') }}</PosUiButton
      >
      <a
        class="open-link"
        :href="url || undefined"
        target="_blank"
        rel="noopener noreferrer"
      >
        <PosUiIcon name="open-in-new" :size="20" />{{
          $t('reservations.link.open')
        }}
      </a>
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="qrcode"
        :disabled="!url || downloading"
        :aria-busy="downloading"
        @click="downloadQr"
        >{{ $t('reservations.link.qr') }}</PosUiButton
      >
    </div>
    <PosUiButton
      class="more-toggle"
      color="neutral"
      variant="ghost"
      :icon="sharingOpen ? 'chevron-up' : 'chevron-down'"
      :aria-expanded="sharingOpen"
      aria-controls="rsv-sharing-instructions"
      @click="sharingOpen = !sharingOpen"
      >{{ $t('reservations.link.whereToShare') }}</PosUiButton
    >
    <div
      v-show="sharingOpen"
      id="rsv-sharing-instructions"
      class="sharing-instructions"
    >
      <section v-for="channel in ['gbp', 'ig']" :key="channel">
        <h3>{{ $t(`reservations.link.${channel}Title`) }}</h3>
        <ol>
          <li v-for="step in 3" :key="step">
            {{ $t(`reservations.link.${channel}Step${step}`) }}
          </li>
        </ol>
      </section>
    </div>
  </PosUiSettingsCard>
</template>

<style scoped lang="scss">
  .link-settings {
    :deep(.card-body) {
      @apply tw-flex tw-flex-col tw-items-start tw-gap-4;
    }
    .reservations-notice {
      @apply tw-mb-0 tw-w-full;
    }
    .link-actions {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    .open-link {
      @apply tw-inline-flex tw-h-11 tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-4 tw-text-sm tw-font-medium tw-text-neutral-700 hover:tw-bg-neutral-100 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
    }
    .sharing-instructions {
      @apply tw-grid tw-w-full tw-gap-5 tw-rounded-xl tw-bg-canvas tw-p-4 sm:tw-grid-cols-2;
      h3 {
        @apply tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900;
      }
      ol {
        @apply tw-mb-0 tw-mt-2 tw-list-decimal tw-space-y-2 tw-pl-5 tw-text-sm tw-leading-relaxed tw-text-neutral-600;
      }
    }
    .visually-hidden {
      @apply tw-sr-only;
    }
  }
</style>
